// Shared formatting for greenhouse stress-profile steps - extracted from
// StartRunConfirmModal.jsx (which was the only place this logic lived)
// so RunProgressTimeline.jsx can reuse it without a second copy.

// Per-step duration: plain minutes, matching the plan's own examples
// ("Hold 40°C / 55% RH for 60 min"). Falls back to whole seconds for a
// sub-minute step rather than rounding it away to "0 min".
export function formatStepDuration(seconds) {
  const minutes = Math.round(seconds / 60);
  if (minutes >= 1) return `${minutes} min`;
  return `${Math.max(0, Math.round(seconds))}s`;
}

// Total estimated duration: hours + minutes ("2h 0m"), readable at a glance
// for runs that span multiple steps/hours.
export function formatTotalDuration(totalSeconds) {
  const totalMinutes = Math.round(totalSeconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${hours}h ${minutes}m`;
}

// Step shape confirmed against StressProfileEditor.jsx/validate_stress_steps
// (padihat/models.py): {kind: "hold"|"ramp", temp_c, rh_pct,
// duration_seconds}. validate_stress_steps also accepts to_temp_c/to_rh_pct
// as an alternate key for the same value - this frontend never produces
// that shape itself, but reading either is a harmless safety net rather
// than silently rendering "undefined°C" if it ever shows up (e.g. a saved
// profile edited directly via the API).
export function describeStep(step) {
  const temp = step.temp_c ?? step.to_temp_c;
  const rh = step.rh_pct ?? step.to_rh_pct;
  const conditionParts = [];
  if (typeof temp === "number") conditionParts.push(`${temp}°C`);
  if (typeof rh === "number") conditionParts.push(`${rh}% RH`);
  const condition = conditionParts.length ? conditionParts.join(" / ") : "current conditions";
  const isRamp = step.kind === "ramp";
  return `${isRamp ? "Ramp to" : "Hold"} ${condition} ${isRamp ? "over" : "for"} ${formatStepDuration(
    step.duration_seconds
  )}`;
}

// Sum of planned durations across all steps - treats a non-array input as
// zero steps rather than throwing, and a non-numeric duration_seconds as 0.
export function sumStepDurations(steps) {
  if (!Array.isArray(steps)) return 0;
  return steps.reduce((sum, step) => sum + (Number(step?.duration_seconds) || 0), 0);
}
